/*
frontend/src/ColumnSelectionModal.js
Updated File:
- Added props `isFilesTableVisible`, `toggleFilesTableVisibility`.
- Added a button in the 'Config' tab to toggle file table visibility.
*/
import React, { useState } from 'react';
import Modal from 'react-modal'; // Assuming you use react-modal
import './App.css'; // Reuse styles if possible

// Ensure Modal root is set (usually in App.js or index.js)
// Modal.setAppElement('#root'); // Example

const ColumnSelectionModal = ({
  isOpen,
  onClose,
  activeTab,               // State for active tab (controlled by parent)
  setActiveTab,            // Setter for active tab (controlled by parent)
  visibleColumns,
  columns,                 // All available columns
  onToggleColumn,          // Function to toggle column visibility
  systemPrompts,
  selectedSystemPrompt,
  setSelectedSystemPrompt,
  // --- Props for Statement Visibility ---
  isStatementsVisible,
  toggleStatementsVisibility,
  // --- NEW Props for Files Table Visibility ---
  isFilesTableVisible,
  toggleFilesTableVisibility
}) => {
  const [showFullText, setShowFullText] = useState(false);

  if (!isOpen) return null;

  const handlePromptSelection = (event) => {
    // Find the full prompt object based on the selected ID
    const promptId = event.target.value;
    const prompt = systemPrompts.find(p => p.id.toString() === promptId);
    // Store the *full text* of the selected prompt
    setSelectedSystemPrompt(prompt?.prompt_text || ''); // Store the text content
    setShowFullText(false); // Reset show full text view
  };

  // Filter out columns that start with 'kn' for the column selection tab
  const filteredColumns = columns.filter(column => !column.startsWith('kn'));

  // Get the ID of the currently selected prompt text for the dropdown value
  const selectedPromptId = systemPrompts.find(p => p.prompt_text === selectedSystemPrompt)?.id || '';

  // --- Modal Styles (Example - Adjust as needed or use CSS file) ---
  const modalStyle = {
    content: { top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '0', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1050 }, // Higher z-index if needed
  };
  const modalHeaderStyle = { padding: '15px 20px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '1.1em', flexShrink: 0 };
  const modalBodyStyle = { padding: '20px', flexGrow: 1, overflowY: 'auto' };
  const modalFooterStyle = { padding: '10px 20px', borderTop: '1px solid #eee', textAlign: 'right', flexShrink: 0 };
  const configButtonStyle = { padding: '8px 12px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', marginLeft: '10px' }; // Basic style


  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyle}
      contentLabel="Review Settings"
      ariaHideApp={false} // Avoids console warning, set appropriately in production
    >
        {/* --- Modal Header --- */}
        <div style={modalHeaderStyle}>
            Review Settings
        </div>

        {/* --- Modal Body (Tabs + Content) --- */}
        <div style={modalBodyStyle}>
            {/* --- Tab Buttons --- */}
            <div className="config-tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #ccc', paddingBottom: '0' }}>
                {/* UPDATED: Added 'Config' tab */}
                {['Select Columns', 'Summary Prompt', 'Config'].map((tab) => (
                <button
                    key={tab}
                    className={`config-tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                    style={{ marginRight: '5px', marginBottom: '-1px' }} // Adjust styling for tabs
                >
                    {tab}
                </button>
                ))}
            </div>

            {/* --- Tab Content --- */}
            <div className="config-page-area" style={{padding: '0', border: 'none', boxShadow: 'none'}}>
                {/* -- Select Columns Tab -- */}
                {activeTab === 'Select Columns' && (
                    <div>
                    <h4>Select Columns to Display</h4>
                    <div style={{ maxHeight: '40vh', overflowY: 'auto', paddingRight: '10px' }}> {/* Scrollable column list */}
                        {filteredColumns.map(col => (
                            <div key={col} style={{ marginBottom: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                type="checkbox"
                                checked={visibleColumns.includes(col)}
                                onChange={() => onToggleColumn(col)}
                                style={{ marginRight: '8px' }}
                                />
                                {col}
                            </label>
                            </div>
                        ))}
                    </div>
                    </div>
                )}

                {/* -- Summary Prompt Tab -- */}
                {activeTab === 'Summary Prompt' && (
                    <div>
                    <h4>Select a System Prompt for Summary</h4>
                    <select
                        onChange={handlePromptSelection}
                        value={selectedPromptId} // Bind value to the ID of the selected prompt
                        style={{ width: '100%', padding: '8px', marginBottom: '15px', border: '1px solid #ccc', borderRadius: '4px'}}
                    >
                        <option value="">-- Default Summary Prompt --</option>
                        {systemPrompts.map(prompt => (
                        <option key={prompt.id} value={prompt.id}>
                            {prompt.prompt_name}
                        </option>
                        ))}
                    </select>

                    {selectedSystemPrompt && (
                        <div>
                        <h5>Selected Prompt Text:</h5>
                        <div style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '4px', padding: '10px', maxHeight: '150px', overflowY: 'auto', position: 'relative' }}>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: '0.9em' }}>
                            {showFullText ? selectedSystemPrompt : `${selectedSystemPrompt.substring(0, 200)}${selectedSystemPrompt.length > 200 ? '...' : ''}`}
                            </pre>
                            {selectedSystemPrompt.length > 200 && (
                            <button
                                onClick={() => setShowFullText(!showFullText)}
                                style={{ position: 'absolute', bottom: '5px', right: '5px', fontSize: '0.8em', padding: '2px 5px', cursor: 'pointer' }}
                                >
                                {showFullText ? 'Show Less' : 'Show More'}
                            </button>
                            )}
                        </div>
                        </div>
                    )}
                    {!selectedSystemPrompt && (
                        <p style={{ fontStyle: 'italic', color: '#666', fontSize: '0.9em' }}>Using default system prompt for summaries.</p>
                    )}
                    </div>
                )}

                {/* --- UPDATED: Config Tab --- */}
                {activeTab === 'Config' && (
                    <div>
                        <h4>View Configuration</h4>
                        {/* Toggle Statements */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '15px', marginBottom: '15px' }}>
                            <span>Statements Section:</span>
                            <button
                                onClick={toggleStatementsVisibility}
                                style={configButtonStyle} // Apply basic button style
                                title={isStatementsVisible ? "Hide the statements section" : "Show the statements section"}
                            >
                                {isStatementsVisible ? 'Hide Statements' : 'Show Statements'}
                            </button>
                        </div>
                        <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px', marginBottom: '20px' }}>
                            Toggle the visibility of the "Statements" list in the main Data Review area.
                        </p>

                        {/* NEW: Toggle Files Table */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '15px', marginBottom: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                            <span>Selected Files Table:</span>
                            <button
                                onClick={toggleFilesTableVisibility} // Call the new toggle function
                                style={configButtonStyle} // Apply basic button style
                                title={isFilesTableVisible ? "Hide the selected files table" : "Show the selected files table"}
                            >
                                {isFilesTableVisible ? 'Hide Table' : 'Show Table'}
                            </button>
                        </div>
                        <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                            Toggle the visibility of the "Selected File Details" table in the main Data Review area.
                        </p>
                        {/* Add more configuration options here as needed */}
                    </div>
                )}
                {/* --- End Config Tab --- */}

            </div> {/* End Tab Content */}
        </div> {/* End Modal Body */}

        {/* --- Modal Footer --- */}
        <div style={modalFooterStyle}>
          <button onClick={onClose} className="modal-close-button">Close</button>
        </div>
    </Modal>
  );
};

export default ColumnSelectionModal;
